import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
    BarChart3,
    BookOpen,
    TrendingUp,
    Shield,
    Download,
    Smartphone,
    Filter,
    CheckCircle,
    Users,
    PieChart,
    FileText,
    Lock,
    ChevronRight,
    Zap,
    Clock,
    Globe,
    Star,
    ArrowRight,
    Menu,
    X
} from 'lucide-react';

const SoftyeExpenseTracker = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [activeFeature, setActiveFeature] = useState(0);
    const [statsVisible, setStatsVisible] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
            if (window.scrollY > 300) setStatsVisible(true);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            setActiveFeature((prev) => (prev + 1) % 4);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const Counter = ({ end, duration = 2000 }) => {
        const [count, setCount] = useState(0);
        useEffect(() => {
            if (!statsVisible) return;
            let start = 0;
            const increment = end / (duration / 16);
            const timer = setInterval(() => {
                start += increment;
                if (start >= end) {
                    setCount(end);
                    clearInterval(timer);
                } else {
                    setCount(Math.floor(start));
                }
            }, 16);
            return () => clearInterval(timer);
        }, [end, duration, statsVisible]);
        return <span>{count}</span>;
    };

    return (
        <div className="min-h-screen bg-white font-sans overflow-x-hidden">
            {/* Navigation */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white/95 backdrop-blur-lg shadow-md' : 'bg-transparent'
                }`}>
                <div className="container mx-auto px-4 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center space-x-3 group cursor-pointer">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300 shadow-lg">
                                <BarChart3 className="h-6 w-6 text-white" />
                            </div>
                            <span className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent">
                                Softye Expense
                            </span>
                        </div>

                        {/* Desktop Menu */}
                        <div className="hidden md:flex items-center gap-8">
                            <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Features</a>
                            <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">How It Works</a>
                            <a href="#use-cases" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">Use Cases</a>
                            <Link to="/login" className="text-gray-700 hover:text-blue-600 font-medium transition-colors">
                                Login
                            </Link>
                            <Link to="/signup" className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-6 py-2.5 rounded-xl font-medium transition-all shadow-lg shadow-blue-600/30 hover:shadow-xl hover:shadow-blue-600/40 hover:scale-105">
                                Start Free
                            </Link>
                        </div>

                        {/* Mobile Menu Button */}
                        <button
                            className="md:hidden text-gray-700"
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                        >
                            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
                        </button>
                    </div>

                    {/* Mobile Menu */}
                    {mobileMenuOpen && (
                        <div className="md:hidden absolute top-full left-0 right-0 bg-white border-t shadow-lg animate-in slide-in-from-top duration-300">
                            <div className="flex flex-col p-4 space-y-4">
                                <a href="#features" className="text-gray-700 hover:text-blue-600 font-medium py-2">Features</a>
                                <a href="#how-it-works" className="text-gray-700 hover:text-blue-600 font-medium py-2">How It Works</a>
                                <a href="#use-cases" className="text-gray-700 hover:text-blue-600 font-medium py-2">Use Cases</a>
                                <Link to="/login" className="text-gray-700 hover:text-blue-600 font-medium py-2">Login</Link>
                                <Link to="/signup" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-xl font-medium text-center">
                                    Start Free
                                </Link>
                            </div>
                        </div>
                    )}
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 overflow-hidden">
                {/* Animated Background */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-purple-50">
                    <div className="absolute top-20 left-10 w-72 h-72 bg-blue-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse"></div>
                    <div className="absolute top-40 right-10 w-72 h-72 bg-purple-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-1000"></div>
                    <div className="absolute -bottom-8 left-1/2 w-72 h-72 bg-pink-400 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse delay-2000"></div>
                </div>

                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-5xl mx-auto text-center">
                        {/* Badge */}
                        <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-8 animate-in fade-in slide-in-from-top duration-700">
                            <Zap className="h-4 w-4" />
                            100% Free for MSMEs • No Credit Card Required
                        </div>

                        {/* Heading */}
                        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-tight animate-in fade-in slide-in-from-bottom duration-700 delay-100">
                            Financial Clarity for Your{' '}
                            <span className="bg-gradient-to-r from-blue-600 via-blue-700 to-purple-600 bg-clip-text text-transparent">
                                Growing Business
                            </span>
                        </h1>

                        <p className="text-lg sm:text-xl md:text-2xl text-gray-600 mb-10 max-w-3xl mx-auto leading-relaxed animate-in fade-in slide-in-from-bottom duration-700 delay-200">
                            Track income, manage expenses, and generate reports effortlessly. Built specifically for MSMEs, freelancers, and shop owners.
                        </p>

                        {/* CTA Buttons */}
                        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8 animate-in fade-in slide-in-from-bottom duration-700 delay-300">
                            <Link to="/signup" className="group bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white px-8 py-4 rounded-xl text-lg font-semibold transition-all shadow-xl shadow-blue-600/30 hover:shadow-2xl hover:shadow-blue-600/40 hover:scale-105 flex items-center justify-center gap-2">
                                Start Free - No Credit Card
                                <ChevronRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
                            </Link>
                            <Link to="/login" className="bg-white hover:bg-gray-50 text-gray-800 px-8 py-4 rounded-xl text-lg font-semibold border-2 border-gray-200 transition-all hover:border-gray-300 hover:shadow-lg">
                                Sign In
                            </Link>
                        </div>

                        {/* Trust Badges */}
                        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600 animate-in fade-in duration-700 delay-400">
                            <div className="flex items-center gap-2">
                                <Shield className="h-5 w-5 text-green-500" />
                                <span>Bank-level Security</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Clock className="h-5 w-5 text-blue-500" />
                                <span>Setup in 2 Minutes</span>
                            </div>
                            <div className="flex items-center gap-2">
                                <Users className="h-5 w-5 text-purple-500" />
                                <span>Trusted by 10,000+ MSMEs</span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Hero Image/Dashboard Preview */}
                <div className="container mx-auto px-4 mt-16 relative z-10 animate-in fade-in slide-in-from-bottom duration-1000 delay-500">
                    <div className="max-w-5xl mx-auto">
                        <div className="relative">
                            <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-purple-600 rounded-3xl blur-3xl opacity-20"></div>
                            <div className="relative bg-white rounded-3xl shadow-2xl p-4 md:p-8 border border-gray-200">
                                <div className="bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border border-blue-100">
                                    <div className="flex justify-between items-center mb-6">
                                        <h3 className="text-xl font-bold text-gray-800">Monthly Dashboard</h3>
                                        <div className="flex items-center gap-2 text-green-600 font-semibold">
                                            <TrendingUp className="h-5 w-5" />
                                            <span>+15.2%</span>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-2 sm:gap-4 mb-6">
                                        <div className="bg-white rounded-xl p-2 sm:p-4 shadow-sm">
                                            <div className="text-gray-500 text-xs sm:text-sm mb-1">Total Income</div>
                                            <div className="text-lg sm:text-2xl font-bold text-gray-900">₹84.5k</div>
                                        </div>
                                        <div className="bg-white rounded-xl p-2 sm:p-4 shadow-sm">
                                            <div className="text-gray-500 text-xs sm:text-sm mb-1">Total Expenses</div>
                                            <div className="text-lg sm:text-2xl font-bold text-gray-900">₹42.3k</div>
                                        </div>
                                        <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-2 sm:p-4 shadow-sm">
                                            <div className="text-green-700 text-xs sm:text-sm mb-1">Net Balance</div>
                                            <div className="text-lg sm:text-2xl font-bold text-green-800">₹42.2k</div>
                                        </div>
                                    </div>
                                    <div className="h-48 flex items-end gap-2">
                                        {[65, 80, 60, 90, 75, 95, 70, 85, 78, 92, 68, 88].map((height, idx) => (
                                            <div key={idx} className="flex-1">
                                                <div
                                                    className="rounded-t-lg bg-gradient-to-t from-blue-600 to-blue-400 transition-all duration-1000 hover:from-blue-700 hover:to-blue-500"
                                                    style={{
                                                        height: `${height}%`,
                                                        animation: `slideUp 0.8s ease-out ${idx * 0.1}s both`
                                                    }}
                                                ></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 bg-gradient-to-br from-blue-600 to-blue-800 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                    <div className="absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl"></div>
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8 max-w-5xl mx-auto">
                        {[
                            { end: 10000, suffix: '+', label: 'Active Users' },
                            { end: 50000, suffix: '+', label: 'Transactions Tracked' },
                            { end: 99, suffix: '%', label: 'Uptime' },
                            { end: 24, suffix: '/7', label: 'Support' }
                        ].map((stat, idx) => (
                            <div key={idx} className="text-center text-white">
                                <div className="text-3xl sm:text-4xl md:text-5xl font-bold mb-2">
                                    <Counter end={stat.end} />
                                    {stat.suffix}
                                </div>
                                <div className="text-blue-100 text-xs sm:text-sm md:text-base">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="py-20 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            POWERFUL FEATURES
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Everything You Need to Succeed
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Simple yet powerful tools designed specifically for small businesses
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
                        {[
                            {
                                icon: <BookOpen className="h-8 w-8" />,
                                title: "Multiple Ledgers",
                                description: "Create separate books for business, projects, personal use, and more. Organize finances your way.",
                                color: "from-blue-500 to-blue-600"
                            },
                            {
                                icon: <TrendingUp className="h-8 w-8" />,
                                title: "Income & Expense Tracking",
                                description: "Log every transaction with amount, category, notes, and payment mode in seconds.",
                                color: "from-green-500 to-green-600"
                            },
                            {
                                icon: <BarChart3 className="h-8 w-8" />,
                                title: "Smart Reports",
                                description: "Generate customizable reports with advanced filters. See exactly what you need.",
                                color: "from-purple-500 to-purple-600"
                            },
                            {
                                icon: <PieChart className="h-8 w-8" />,
                                title: "Visual Dashboard",
                                description: "Get instant insights with beautiful charts and graphs. Understand your finances at a glance.",
                                color: "from-orange-500 to-orange-600"
                            },
                            {
                                icon: <Download className="h-8 w-8" />,
                                title: "Export Data",
                                description: "Download reports in JSON or CSV format. Share with accountants or backup your data.",
                                color: "from-pink-500 to-pink-600"
                            },
                            {
                                icon: <Smartphone className="h-8 w-8" />,
                                title: "Works Everywhere",
                                description: "Access from any device - desktop, tablet, or mobile. Your data syncs automatically.",
                                color: "from-indigo-500 to-indigo-600"
                            }
                        ].map((feature, idx) => (
                            <div
                                key={idx}
                                className="group bg-white rounded-2xl p-8 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-blue-200 hover:-translate-y-2"
                            >
                                <div className={`w-16 h-16 bg-gradient-to-br ${feature.color} rounded-2xl flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section id="how-it-works" className="py-20 bg-white">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <div className="inline-block bg-green-100 text-green-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            SIMPLE SETUP
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Start in 3 Easy Steps
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            Be up and running in less than 2 minutes
                        </p>
                    </div>

                    <div className="max-w-4xl mx-auto">
                        {[
                            {
                                step: "01",
                                title: "Create Your Account",
                                description: "Sign up for free in seconds. No credit card needed, no complicated forms.",
                                icon: <Users className="h-8 w-8" />
                            },
                            {
                                step: "02",
                                title: "Add Your First Transaction",
                                description: "Log an income or expense entry. Add amount, category, and notes - that's it!",
                                icon: <FileText className="h-8 w-8" />
                            },
                            {
                                step: "03",
                                title: "Track & Analyze",
                                description: "View your dashboard, generate reports, and make smarter financial decisions.",
                                icon: <BarChart3 className="h-8 w-8" />
                            }
                        ].map((step, idx) => (
                            <div key={idx} className="relative mb-12 last:mb-0">
                                <div className="flex flex-col md:flex-row items-start gap-8">
                                    <div className="flex-shrink-0">
                                        <div className="w-20 h-20 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl flex items-center justify-center text-white text-2xl font-bold shadow-lg">
                                            {step.step}
                                        </div>
                                    </div>
                                    <div className="flex-1 bg-gradient-to-br from-gray-50 to-white rounded-2xl p-8 border border-gray-200 hover:shadow-lg transition-all duration-300">
                                        <div className="flex items-start gap-4">
                                            <div className="flex-shrink-0 w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center text-blue-600">
                                                {step.icon}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="text-2xl font-bold text-gray-900 mb-2">{step.title}</h3>
                                                <p className="text-gray-600 text-lg leading-relaxed">{step.description}</p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {idx < 2 && (
                                    <div className="hidden md:block absolute left-10 top-24 w-0.5 h-12 bg-gradient-to-b from-blue-600 to-blue-300"></div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Use Cases */}
            <section id="use-cases" className="py-20 bg-gradient-to-br from-blue-50 to-purple-50">
                <div className="container mx-auto px-4">
                    <div className="text-center mb-16">
                        <div className="inline-block bg-purple-100 text-purple-700 px-4 py-2 rounded-full text-sm font-semibold mb-4">
                            WHO IT'S FOR
                        </div>
                        <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
                            Built for Every Small Business
                        </h2>
                        <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                            From retail to freelancing, we've got you covered
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {[
                            { industry: "🏪 Retail Shops", use: "Track daily sales, supplier payments, and shop expenses" },
                            { industry: "💼 Freelancers", use: "Separate project costs, track client payments, manage taxes" },
                            { industry: "🔧 Service Providers", use: "Monitor service costs, client invoices, and operations" },
                            { industry: "⚙️ Workshops & Repair", use: "Track inventory, labor costs, and equipment maintenance" },
                            { industry: "🏭 Small Manufacturers", use: "Monitor raw materials, production costs, and sales" },
                            { industry: "🌾 Farmers", use: "Separate crop cycles, track inputs, and market sales" }
                        ].map((useCase, idx) => (
                            <div
                                key={idx}
                                className="group bg-white rounded-2xl p-6 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-purple-200 hover:-translate-y-2"
                            >
                                <h3 className="text-xl font-bold text-gray-900 mb-3 flex items-center gap-2">
                                    {useCase.industry}
                                </h3>
                                <p className="text-gray-600 leading-relaxed">{useCase.use}</p>
                                <div className="mt-4 flex items-center text-blue-600 font-semibold text-sm group-hover:gap-2 transition-all">
                                    Learn more <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-24 bg-gradient-to-br from-blue-600 via-blue-700 to-purple-700 relative overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute top-20 left-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-20 right-20 w-96 h-96 bg-white rounded-full blur-3xl animate-pulse delay-1000"></div>
                </div>
                <div className="container mx-auto px-4 relative z-10">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="inline-block bg-white/20 backdrop-blur-sm text-white px-6 py-2 rounded-full text-sm font-semibold mb-6">
                            🎉 Special Offer for MSMEs
                        </div>
                        <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
                            100% FREE Forever
                        </h2>
                        <p className="text-xl md:text-2xl text-blue-100 mb-12 max-w-3xl mx-auto leading-relaxed">
                            No hidden costs. No credit card. No trials. Just powerful expense tracking for your business.
                        </p>

                        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-2xl max-w-3xl mx-auto">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4 mb-8">
                                {[
                                    "✓ Unlimited transactions",
                                    "✓ Multiple ledgers",
                                    "✓ Custom categories",
                                    "✓ Advanced reports",
                                    "✓ Export to CSV/JSON",
                                    "✓ Mobile & desktop",
                                    "✓ Bank-level security",
                                    "✓ Email support"
                                ].map((item, idx) => (
                                    <div key={idx} className="flex items-center gap-2 sm:gap-3 text-left">
                                        <CheckCircle className="h-4 w-4 sm:h-5 sm:w-5 text-green-500 flex-shrink-0" />
                                        <span className="text-sm sm:text-base text-gray-700 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>

                            <Link
                                to="/signup"
                                className="group w-full inline-flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white py-5 rounded-2xl text-xl font-bold transition-all shadow-xl shadow-blue-600/30 hover:shadow-2xl hover:shadow-blue-600/40 hover:scale-105"
                            >
                                Start Tracking Now - It's FREE!
                                <ChevronRight className="h-6 w-6 ml-2 group-hover:translate-x-1 transition-transform" />
                            </Link>

                            <p className="text-gray-500 text-sm mt-6">
                                No credit card • No time limit • Made for MSMEs in India
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-gray-900 text-white py-16">
                <div className="container mx-auto px-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-8 sm:gap-10 md:gap-12 mb-12">
                        <div>
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-blue-700 rounded-xl flex items-center justify-center">
                                    <BarChart3 className="h-6 w-6 text-white" />
                                </div>
                                <span className="text-xl font-bold">Softye Expense</span>
                            </div>
                            <p className="text-gray-400 leading-relaxed text-sm sm:text-base">Financial clarity for growing businesses. 100% FREE for MSMEs.</p>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4 text-base sm:text-lg">Quick Links</h4>
                            <ul className="space-y-2 sm:space-y-3 text-gray-400 text-sm sm:text-base">
                                <li><Link to="/signup" className="hover:text-white transition-colors">Sign Up Free</Link></li>
                                <li><Link to="/login" className="hover:text-white transition-colors">Login</Link></li>
                                <li><a href="#features" className="hover:text-white transition-colors">Features</a></li>
                                <li><a href="#use-cases" className="hover:text-white transition-colors">Use Cases</a></li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4 text-base sm:text-lg">Contact</h4>
                            <ul className="space-y-2 sm:space-y-3 text-gray-400 text-sm sm:text-base">
                                <li>info@softyetech.com</li>
                                <li>+91 7448539858</li>
                                <li>Erode, Tamil Nadu</li>
                                <li>India</li>
                            </ul>
                        </div>

                        <div>
                            <h4 className="font-semibold mb-4 text-base sm:text-lg">Legal</h4>
                            <ul className="space-y-2 sm:space-y-3 text-gray-400 text-sm sm:text-base">
                                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Terms of Service</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Data Security</a></li>
                                <li><a href="#" className="hover:text-white transition-colors">Help Center</a></li>
                            </ul>
                        </div>
                    </div>

                    <div className="pt-8 border-t border-gray-800 text-center text-gray-400">
                        <p>© 2024 Softye Technologies. All rights reserved. Made with ❤️ for MSMEs in India.</p>
                    </div>
                </div>
            </footer>

            {/* Fixed CTA for Mobile */}
            <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 shadow-2xl md:hidden z-40 animate-in slide-in-from-bottom duration-300">
                <Link
                    to="/signup"
                    className="block w-full bg-gradient-to-r from-blue-600 to-blue-700 text-white py-3 rounded-xl font-semibold text-center shadow-lg"
                >
                    Start Free - No Credit Card
                </Link>
            </div>

            <style jsx>{`
                @keyframes slideUp {
                    from {
                        height: 0;
                        opacity: 0;
                    }
                    to {
                        opacity: 1;
                    }
                }
            `}</style>
        </div>
    );
};

export default SoftyeExpenseTracker;